import {
  FluidRowData,
  FluidTableHeader,
  FluidTableRowData,
} from '@lmig/fluid-core';

import { FILTERED, ID } from '../../../constants/constants';
import { Utils } from '../../../utils/utils';
import { searchDataSourceAsync } from '../features/search/fluid-table-search.worker';
import {
  applyTypeFormatting,
  getDataAtPath,
} from './fluid-table-data-management.utils';
import { stripFluidData } from './fluid-table-editable-cell-management.utils';
import { flattenHeaders } from './fluid-table-header.utils';

/**
 * Search the datasource for the given searchTerm at any datapoint.
 * @param dataSource
 * @param headers
 * @param searchTerm
 * @param searchableColumns
 * @param excludeFiltered
 * @param deepSearch
 */
export const searchDataSource = <DataItem extends FluidTableRowData<DataItem>>(
  dataSource: DataItem[],
  headers: FluidTableHeader[],
  searchTerm: string | number,
  searchableColumns: string[],
  excludeFiltered = false,
  deepSearch = false
): Promise<any> => {
  const flattenedHeaders = flattenHeaders(headers);
  let searchableHeaders =
    searchableColumns?.length > 0
      ? flattenedHeaders.filter((header) =>
          searchableColumns.includes(header.dataPath)
        )
      : flattenedHeaders;

  if (deepSearch) {
    // get all headers based on dataSource
    searchableHeaders = getHeadersFromSource(dataSource[0]);
  }

  const startGDP = performance.now();
  const dataPoints = gatherDataPoints(
    dataSource,
    searchableHeaders,
    excludeFiltered
  );
  Utils.logPerformance('gatherDataPoints', startGDP);
  return searchDataSourceAsync(dataSource, searchTerm, dataPoints);
};

/**
 * Gathers the data points for the searchable headers from the entire datasource.
 * @param dataSource
 * @param searchableHeaders
 * @param excludeFiltered
 */
const gatherDataPoints = <DataItem extends FluidTableRowData<DataItem>>(
  dataSource: DataItem[],
  searchableHeaders: FluidTableHeader[],
  excludeFiltered: boolean
) => {
  return dataSource.reduce((dataPoints: Record<string, string[]>, dataItem) => {
    if (shouldIncludeInSearch(dataItem, excludeFiltered)) {
      dataPoints[dataItem[ID]] = gatherSearchableDataPoints(
        searchableHeaders,
        dataItem
      );
    }
    return dataPoints;
  }, {});
};

/**
 * Returns true if the dataItem should be included in the search
 * @param dataItem
 * @param excludeFiltered
 */
const shouldIncludeInSearch = (
  dataItem: FluidRowData,
  excludeFiltered: boolean
): boolean => !excludeFiltered || (excludeFiltered && !dataItem[FILTERED]);

/**
 * Returns the value set from the given dataItem from the searchable headers
 * @param searchableHeaders
 * @param dataItem
 */
const gatherSearchableDataPoints = <
  DataItem extends FluidTableRowData<DataItem>
>(
  searchableHeaders: FluidTableHeader[],
  dataItem: DataItem
) =>
  searchableHeaders.reduce((values: string[], header: FluidTableHeader) => {
    let formattedValue: any,
      searchableValues: any[] = [];
    const dataValue = header.useFullDataItem
      ? dataItem
      : getDataAtPath(dataItem, header);
    if (dataValue) {
      formattedValue = header.dataFormat
        ? header.dataFormat(header.useFullDataItem ? dataItem : dataValue)
        : header.formattingOptions
        ? applyTypeFormatting(header, dataValue)
        : dataValue;
      searchableValues = formattedValue
        ? [getDataAtPath(dataItem, header), formattedValue]
        : [getDataAtPath(dataItem, header)];
    }
    return Array.from(new Set([...values, ...searchableValues]));
  }, []);

const getHeadersFromSource = (dataSource: FluidRowData) =>
  Object.keys(stripFluidData(dataSource)).map((item) => {
    return { dataPath: item, label: item } as FluidTableHeader;
  });
